import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import notify from '~/components/Notify';
import { FarmersService } from '~/api/services/farmers/index'
import FarmerGroupsService from '~/api/services/farmerGroups'
import { RouteTypes } from '~/routes/constants'
import Layout from '~/components/Layout'
import Image from './Forms/Image'
import GeneralInfo from './Forms/GeneralInfo'
import Location from './Forms/Location'
import AmountFarmerGroup from './Forms/AmountFarmerGroup'
import { formatNumber, handleErrors } from '~/utils/helpers'
import FormWrapper from './FormWrapper'
import { useAuth } from '~/routes/components/AuthProvider'
import { FAT_PROTEIN_SLUG, MILK_FIXED_SLUG, MILK_STOCK_EXCHANGE } from '~/Constants'

const tradingModulesSlug = [MILK_FIXED_SLUG, FAT_PROTEIN_SLUG, MILK_STOCK_EXCHANGE]

const CreateEditFarmers: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { systemData: { modules } } = useAuth()
  const navigate = useNavigate()
  dayjs.extend(localeData)
  dayjs().localeData()

  const [isBusy, setIsBusy] = useState(false)
  const [farmersOptions, setFarmersOptions] = useState(null)
  const [farmerGroupList, setFarmerGroupList] = useState([])
  const [farmersList, setFarmersList] = useState([])

  const hasAnyTradingModules = Boolean(modules.find(module => !!tradingModulesSlug.find(tradingSlotSlug => tradingSlotSlug === module.slug)))

  const methods = useForm({
    mode: 'onSubmit'
  })

  useEffect(() => {
    fetchData()
  }, [t])

  async function fetchData() {
    setIsBusy(true)
    try {
      const responseProfileData = await FarmersService.getFarmerOptions()
      const farmerGroupsListResponse = await FarmerGroupsService.getFarmerGroupsList()
      const farmersResponse = await FarmersService.getFarmers({ isMain: 1, page: 0, search: '' })
      if (id) {
        const response = await FarmersService.getFarmer(id)
        if (!response.data.districtId && response.data.zip) {
          const zipData = await FarmersService.getZipDistrictState({ zip: response.data.zip })
          response.data.districtId = zipData.data.id
        }
        const fileResponse = await FarmersService.getFarmerFile(id)
        response.data.amounts = addMonthsAndAmounts(response.data.amounts)
        methods.reset({
          ...response.data,
          farmerType: response.data.parentId ? 'sub' : 'main',
          terminationDate: response.data?.terminationDate ? dayjs(response.data?.terminationDate).format('DD.MM.YYYY') : response.data?.terminationDate,
          lastDeliveryDate: response.data?.lastDeliveryDate ? dayjs(response.data?.lastDeliveryDate).format('DD.MM.YYYY') : response.data?.lastDeliveryDate
        })
        methods.setValue('logo', fileResponse.data)
      } else if (hasAnyTradingModules) {
        methods.setValue('amounts', addMonthsAndAmounts([]))
      }
      setFarmersList(farmersResponse.data)
      if (responseProfileData.data.defaultCountry && !id) {
        methods.setValue('countryId', responseProfileData.data.defaultCountry)
      }
      setFarmerGroupList(farmerGroupsListResponse.data.map(farmerGroup => ({
        value: farmerGroup.id,
        text: `${farmerGroup.name} (${formatNumber(farmerGroup.amountPercent)}%)`
      })))
      setFarmersOptions(responseProfileData.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function addMonthsAndAmounts(data) {
    const lastMonthIndex = dayjs().month() - 1
    const monthsAndAmounts = []
    for (let i = lastMonthIndex, j = 0; i >= 0; i--, j++) {
      const date = dayjs(new Date(`${dayjs().year()}-${i + 1 >= 10 ? i + 1 : `0${i + 1}`}-01`)).format('YYYY-MM-DD')
      const findIndexAmount = id ? data.findIndex(amount => amount.month === date) : -1
      monthsAndAmounts.push({
        month: date,
        amount: findIndexAmount >= 0 ? data[findIndexAmount].amount : ''
      })
    }
    for (let i = 11, j = lastMonthIndex + 1; i > lastMonthIndex; i--, j++) {
      const date = dayjs(new Date(`${dayjs().year() - 1}-${i + 1 >= 10 ? i + 1 : `0${i + 1}`}-01`)).format('YYYY-MM-DD')
      const findIndexAmount = id ? data.findIndex(amount => amount.month === date) : -1
      monthsAndAmounts.push({
        month: date,
        amount: findIndexAmount >= 0 ? data[findIndexAmount].amount : ''
      })
    }
    return monthsAndAmounts
  }

  function parseDate(value) {
    const arrayOfValues = value.split('.')
    return `${arrayOfValues[2]}-${arrayOfValues[1]}-${arrayOfValues[0]}`
  }

  async function onSubmit(data) {
    if (data.amountType === 'year') {
      delete data.amounts
    } else {
      delete data.amount
    }
    if (!id && data?.farmerType === 'sub') {
      delete data.farmerGroupId
    }
    data.terminationDate = data?.terminationDate ? parseDate(data.terminationDate) : data?.terminationDate
    data.lastDeliveryDate = data?.lastDeliveryDate ? parseDate(data.lastDeliveryDate) : data?.lastDeliveryDate
    setIsBusy(true)
    try {
      if (!id) {
        await FarmersService.createFarmer(data)
        notify(t('Farmer created'), 'success')
      } else {
        await FarmersService.updateFarmer(id, data)
        notify(t('Farmer updated'), 'success')
      }
      navigate(RouteTypes.DAIRY_FACTORY_FARMERS)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderFarmerForm() {
    return <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <FormWrapper header={t('Farmer information')}>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <Image />
          <GeneralInfo
            farmersOptions={farmersOptions}
            farmersList={farmersList}
            farmerGroupList={farmerGroupList}
          />
        </div>
      </FormWrapper>
      <FormWrapper header={t('Location information')}>
        <Location farmersOptions={farmersOptions} />
      </FormWrapper>
      {hasAnyTradingModules && <FormWrapper header={t('Quantities delivered (in kg)')}>
        <AmountFarmerGroup
          farmersOptions={farmersOptions}
        />
      </FormWrapper>}
    </div>
  }

  return <Layout
    isBusy={isBusy}
    segmented
    close={RouteTypes.DAIRY_FACTORY_FARMERS}
    title={id ? t('Edit farmer') : t('Create farmer')}
    action={id ? t('Update') : t('Save')}
    onSubmit={methods.handleSubmit(onSubmit)}
  >
    <FormProvider {...methods}>
      {farmersOptions && !isBusy && renderFarmerForm()}
    </FormProvider>
  </Layout>

}
export default CreateEditFarmers
